import { useCallback, useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';

/**
 * Keeps unsaved clinical work from being lost by navigating away.
 *
 * Inside the app a navigation is held until the user confirms; closing or reloading the tab asks the browser's own question. Nothing is written to browser storage as a fallback — clinical values never leave memory except in a save. `allowNextNavigation` lets a deliberate move through, such as opening the saved examination after its first save.
 */
export function useUnsavedChangesGuard(isDirty: boolean) {
  const dirty = useRef(isDirty);
  const allowed = useRef(false);
  dirty.current = isDirty;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !allowed.current && dirty.current && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!isDirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  const allowNextNavigation = useCallback(() => {
    allowed.current = true;
  }, []);
  const resetAllowance = useCallback(() => {
    allowed.current = false;
  }, []);

  return {
    isBlocked: blocker.state === 'blocked',
    stay: () => blocker.reset?.(),
    leave: () => blocker.proceed?.(),
    allowNextNavigation,
    resetAllowance,
  };
}
